import InApp from 'detect-inapp';
import CheckoutLocale from 'locale/en/checkout.json';
import LoginLocale from 'locale/en/login.json';
import { ShippingMethod, shippingMethodCodeForCountry } from 'lib/shipping';
import { ErrorsDictionary } from 'entities/errors';
import { AddressType } from 'entities/address-types';
import { buildMetaUserData, propertiesForProduct } from 'lib/analytics';
import { getCurrentCurrency } from 'lib/utils/current-currency';
import { PaymentType } from 'entities/payment-types';
import { Product } from 'entities/product';
import { CustomerNotes, PurchaseData } from './entities';
import { User } from 'entities/user';
import { validateEmail } from 'lib/utils/validate-email';
import { parseCookies, setCookie } from 'nookies';
import { v4 as uuidv4 } from 'uuid';

const CHECKOUT_ID_NAME = 'seed_checkout_id';
export const getCheckoutId = (): string => {
  const cookies = parseCookies();
  let checkoutId = cookies[CHECKOUT_ID_NAME];
  if (typeof checkoutId === 'undefined') {
    checkoutId = uuidv4();
    setCookie(null, CHECKOUT_ID_NAME, checkoutId);
  }
  return checkoutId;
};

export const sanitizeAddress = (address): AddressType => {
  const sanitized = {
    street1: address.street1 || address.street_1 || '',
    street2: address.street2 || address.street_2 || '',
    company: address.company || '',
    city: address.city || '',
    region: address.region || '',
    postalCode: address.postalCode || address.postal_code || '',
    country: address.country || '',
  };

  if (address.phone) {
    sanitized['phone'] = address.phone || '';
  }
  if (address.firstName || address.first_name) {
    sanitized['firstName'] = address.firstName || address.first_name || '';
  }
  if (address.lastName || address.last_name) {
    sanitized['lastName'] = address.lastName || address.last_name || '';
  }

  return sanitized;
};

export const checkUserFormErrors = (userEmail, userPassword) => {
  const errorMessages: any = [];

  if (!validateEmail(userEmail)) {
    errorMessages.push(LoginLocale.error_email);
  }

  if (userPassword.trim().length < 6) {
    errorMessages.push(LoginLocale.error_password);
  }

  return errorMessages;
};

export function isUsingApplePay(paymentMethod: string): boolean {
  return paymentMethod === 'applePay';
}

type ShippingFee = {
  method_code?: string;
  amount?: number;
};

type Subscription = {
  planCode: string;
  quantity: number;
  shipping?: ShippingFee;
  unitAmount?: number;
};

export const getSubscriptionItems = (
  currentCartItems,
  products: Array<Product>,
  country?,
  includeUnitPrice?: boolean,
  shippingMethod?: ShippingMethod,
) => {
  const subscriptions: Array<Subscription> = [];
  const cartProducts = Object.keys(currentCartItems);
  cartProducts.forEach((cartProductCode) => {
    const product = products.find(({ code }) => code === cartProductCode);
    if (product) {
      const sub: Subscription = {
        planCode: cartProductCode,
        quantity: currentCartItems[cartProductCode],
      };

      if (includeUnitPrice) {
        sub.unitAmount = parseFloat(product.price);
      }

      if (shippingMethod) {
        sub.shipping = {
          method_code: shippingMethod,
        };
      } else if (country) {
        sub.shipping = {
          method_code: shippingMethodCodeForCountry(country),
        };
      }

      subscriptions.push(sub);
    }
  });
  return subscriptions;
};

export function checkPaymentFormErrors(input): ErrorsDictionary {
  const { cardError, invalidPaymentToken, paymentMethod, paypalToken } = input;

  const errorMessages: any = {};
  if (paymentMethod === 'paypal') {
    if (!paypalToken) {
      errorMessages.payPal = CheckoutLocale.error_paypal_token;
    }
  }

  if (invalidPaymentToken) {
    errorMessages.invalidPaymentToken = CheckoutLocale.error_cc_number;
  }

  if (cardError) {
    cardError.fields.forEach((field) => {
      switch (field) {
        case 'number':
          errorMessages.number = CheckoutLocale.error_cc_number;
          break;
        case 'cvv':
          errorMessages.cvv = CheckoutLocale.error_cc_cvv;
          break;
        case 'month':
          errorMessages.month = CheckoutLocale.error_cc_month;
          break;
        case 'year':
          errorMessages.year = CheckoutLocale.error_cc_year;
          break;
        default:
          break;
      }
    });
  }

  return errorMessages;
}

export const productsForAnalytics = (userCart, products) => {
  return Object.keys(userCart)
    .map((code) => {
      const product = products.find((prod) => prod.code === code);
      if (!product) return null;
      return propertiesForProduct(product, userCart[code]);
    })
    .filter(Boolean);
};

export const buildCheckoutStartedData = async ({
  userCart,
  products,
  promoCode,
  userState,
  method,
  location,
}: {
  userCart: { cart: unknown; getTotalPrice: () => number };
  products: Product[];
  promoCode: string;
  userState?: User;
  method?: string;
  location?: string;
}) => {
  const data: Record<string, unknown> = {
    checkout_id: getCheckoutId(),
    affiliation: 'Seed',
    value: userCart.getTotalPrice(),
    revenue: userCart.getTotalPrice(),
    shipping: 0, // TODO: Use shipping if applicable
    tax: 0, // TODO: Use tax
    discount: 0, // TODO: Use discount amount
    coupon: promoCode,
    currency: getCurrentCurrency(),
    products: productsForAnalytics(userCart.cart, products),
  };
  if (method) {
    data['method'] = method;
  }
  if (location) {
    data['location'] = location;
  }
  const userData = await buildMetaUserData(userState);
  if (userData) {
    data['userData'] = userData;
  }
  return data;
};

export function formatPurchaseData(
  token: string | null,
  payType: PaymentType,
  userState,
  shippingAddress,
  hasOptedInToEmail,
  currentCartItems,
  products,
  couponProperties,
  shippingMethod: ShippingMethod | undefined,
  // References to promoCode directly can be removed after PPv1 is removed
  promoCode?: string,
  isUsingExistingPaymentMethod: boolean = false,
) {
  const userInfo = {
    email: userState.email,
    lastName: userState.lastName,
    firstName: userState.firstName,
    accountId: userState.account_id,
  };

  const shipping = shippingAddress.address_id
    ? { address_id: shippingAddress.address_id }
    : { address: shippingAddress };

  if (shippingMethod) {
    shipping['shippingMethod'] = shippingMethod;
  }

  const customerNotes: CustomerNotes = { accepts_marketing: hasOptedInToEmail };
  const subscriptions = getSubscriptionItems(
    currentCartItems,
    products,
    shippingAddress.country,
    undefined,
    shippingMethod,
  );

  const purchaseData: PurchaseData = {
    userInfo,
    currency: 'USD',
    account: {},
    shipping,
    subscriptions,
    lineItems: [],
    customerNotes: JSON.stringify(customerNotes),
  };

  const appliedCoupons = couponProperties?.filter(
    (coupon) => coupon?.coupon_was_applied === true,
  );

  if (appliedCoupons) {
    purchaseData.couponCodes = appliedCoupons.map(
      (coupon) => coupon.coupon_code,
    );
  } else if (promoCode) {
    purchaseData.couponCodes = [promoCode];
  }

  // Only include billingInfo if we have a token (new payment method)
  // When token is null (using existing payment), backend will use saved payment method
  if (token && !isUsingExistingPaymentMethod) {
    const tokenObject = { token_id: token };
    purchaseData.account.billingInfo = tokenObject;
  }

  return purchaseData;
}

export const isInApp: () => boolean = () => {
  const inapp = new InApp(navigator.userAgent || navigator.vendor);
  return inapp.isInApp;
};

export const getMostRecentlyUsedAddress = (userState: User) => {
  const sortedAddresses = (
    userState?.account?.details?.shipping_addresses || []
  )
    .slice()
    .sort((a, b) => {
      const dateA = new Date(a.updated_at).getTime();
      const dateB = new Date(b.updated_at).getTime();
      return dateB - dateA;
    });

  return sortedAddresses[0] || null;
};
